import { GetUserResponse, VerifyPhoneRequest } from '@/api';
import { useCallback, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getAppCheckLimitedUseTokenHeaders, isFirebaseError } from '@/firebase';
import {
    getFallbackErrorKey,
    getRetryAfterSeconds,
    isApiError
} from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';
import { addOrUpdateCompanyUsersCache } from '../queries/company-cache';

type UseVerifyCompanyUserPhoneActionProps = {
    /**
     * Which of the member's two numbers this call is for — `primary` is
     * `phone`, `secondary` is `phone2`. The two endpoints are the same call
     * against a different field, so the slot is fixed for the hook's lifetime
     * rather than chosen per call.
     */
    slot: 'primary' | 'secondary';
    /**
     * What to say when the number is stored, as a whole sentence — "Secondary
     * phone verified" rather than "Phone verified". Spanish agrees the
     * participle with the noun, so this can never be assembled from a template.
     * Defaults to the generic.
     */
    successMessage?: string;
};

/** The member being verified, and the code they were sent. */
type VerifyPhoneVariables = {
    companyUserId: string;
    data: VerifyPhoneRequest;
};

type UseVerifyCompanyUserPhoneActionData = {
    verifyPhone: (
        companyUserId: string,
        data: VerifyPhoneRequest
    ) => Promise<GetUserResponse>;
    loading: boolean;
    error: string;
};

/**
 * Confirming the code sent by `useAddCompanyUserPhoneAction`, which is what
 * actually stores the number against the member.
 *
 * Carries no `isAllowed` of its own: whether the viewer may edit this member is
 * `useEditProfileAction`, which gates the whole page of sections.
 */
export function useVerifyCompanyUserPhoneAction(
    props: UseVerifyCompanyUserPhoneActionProps
): UseVerifyCompanyUserPhoneActionData {
    const { slot, successMessage } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/users/{id}/phone/verify` and
    // `POST /v3/users/{id}/phone2/verify` declare gets a branch — the two share
    // one error union.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            const seconds = getRetryAfterSeconds(error);

            if (
                isFirebaseError(error) ||
                isApiError(error, 'APP_CHECK_FAILED')
            ) {
                return t('shared.errors.app_check_failed');
            }
            if (isApiError(error, 'INCORRECT_CODE')) {
                return t('shared.errors.invalid_phone_verification_code');
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS') && seconds !== null) {
                return t('shared.errors.too_many_verification_attempts', {
                    seconds
                });
            }
            if (isApiError(error, 'TOO_MANY_REQUESTS')) {
                return t('shared.errors.too_many_requests');
            }

            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('shared.errors.user_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: ({ companyUserId, data }: VerifyPhoneVariables) =>
            // The limited-use token, not the cached one: each attempt is a
            // separate call, and a replayed App Check token would fail the
            // second.
            getAppCheckLimitedUseTokenHeaders()
                .then((headers) =>
                    slot === 'secondary'
                        ? openApiRef.current.users.verifyUserPhone2(
                              companyUserId,
                              data,
                              { headers }
                          )
                        : openApiRef.current.users.verifyUserPhone(
                              companyUserId,
                              data,
                              { headers }
                          )
                )
                .then((response) => response.data),
        onSuccess: (data) => {
            addOrUpdateCompanyUsersCache(queryClient, data.data.user);

            UIToast.success(
                successMessage ?? t('shared.toasts.phone_verified')
            );
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        verifyPhone: (
            companyUserId: string,
            data: VerifyPhoneRequest
        ): Promise<GetUserResponse> =>
            mutation.mutateAsync({ companyUserId, data }),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
